import { useEffect, useState } from 'react';
import { FlaskConical } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  useToast,
} from '@mustauto/ui/web';
import {
  adjustmentFromEdits,
  formatApiError,
  formatStockQuantity,
  isValidStockQuantity,
  parseStockQuantityText,
  stockUnitShort,
} from '@mustauto/utils';
import { api } from '@/lib/api';
import type { OSConsumable } from '@/hooks/useServiceOrders';

const INPUT = 'w-24 rounded-md border border-input bg-background px-2 py-1 text-right text-sm tabular-nums';

/**
 * Insumos do pacote (F2.6) no detalhe da OS: o que a receita dos pacotes diz,
 * o que vai baixar do estoque e o saldo de cada item. Com a OS aberta o gestor
 * ajusta ("hoje foram 40 ml"); fechada, é o registro do que saiu da prateleira.
 * Insumo não é cobrança — já está no preço do pacote.
 */
export function OSConsumablesCard({
  osId,
  consumables,
  closed,
  canEdit,
}: {
  osId: string;
  consumables: OSConsumable[];
  closed: boolean;
  canEdit: boolean;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (!editing) setDraft(Object.fromEntries(consumables.map((c) => [c.inventoryItemId, String(c.quantity)])));
  }, [consumables, editing]);

  const save = useMutation({
    mutationFn: async (items: { inventoryItemId: string; quantity: number }[] | null) =>
      api.put(`/service-orders/${osId}/consumables`, { items }),
    onSuccess: () => {
      setEditing(false);
      void queryClient.invalidateQueries({ queryKey: ['service-order', osId] });
      toast({ type: 'success', title: 'Insumos ajustados' });
    },
    onError: (err) => {
      toast({ type: 'error', title: 'Não foi possível ajustar', description: formatApiError(err, 'Tente de novo em instantes.') });
    },
  });

  if (consumables.length === 0) return null;

  const parsed = new Map(consumables.map((c) => [c.inventoryItemId, parseStockQuantityText(draft[c.inventoryItemId] ?? '')]));
  // Zero vale ("não usei"); vazio, negativo ou mais de 3 casas, não.
  const valid = Array.from(parsed.values()).every((q) => q === 0 || isValidStockQuantity(q));
  const anyAdjusted = consumables.some((c) => c.adjusted);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <FlaskConical size={16} aria-hidden />
          Insumos do pacote
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          {closed
            ? 'Baixados do estoque na conclusão da OS.'
            : 'Baixam do estoque sozinhos quando a OS for concluída. Gastou diferente? Ajuste antes de concluir.'}
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Insumo</TableHead>
              <TableHead className="text-right">Receita</TableHead>
              <TableHead className="text-right">{closed ? 'Baixado' : 'Vai baixar'}</TableHead>
              {!closed && <TableHead className="text-right">Em estoque</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {consumables.map((c) => (
              <TableRow key={c.inventoryItemId}>
                <TableCell>{c.name}</TableCell>
                <TableCell className="text-right tabular-nums text-muted-foreground">
                  {c.recipeQuantity > 0 ? formatStockQuantity(c.recipeQuantity, c.unit) : '—'}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {editing ? (
                    <span className="inline-flex items-center gap-1">
                      <input
                        aria-label={`Quantidade de ${c.name}`}
                        type="number"
                        min={0}
                        step="any"
                        value={draft[c.inventoryItemId] ?? ''}
                        onChange={(e) => setDraft((prev) => ({ ...prev, [c.inventoryItemId]: e.target.value }))}
                        className={INPUT}
                      />
                      <span className="w-6 text-left text-muted-foreground">{stockUnitShort(c.unit)}</span>
                    </span>
                  ) : (
                    <span className={c.adjusted ? 'font-medium' : undefined}>
                      {formatStockQuantity(c.quantity, c.unit)}
                      {c.adjusted && <span className="ml-1 text-xs text-muted-foreground">(ajustado)</span>}
                    </span>
                  )}
                </TableCell>
                {!closed && (
                  <TableCell
                    className={`text-right tabular-nums ${c.available < c.quantity ? 'text-amber-700 dark:text-amber-300' : 'text-muted-foreground'}`}
                  >
                    {formatStockQuantity(c.available, c.unit)}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {!closed && canEdit && (
          <div className="flex flex-wrap gap-2">
            {editing ? (
              <>
                <button
                  type="button"
                  disabled={!valid || save.isPending}
                  // Só o que mudou em relação à receita: o resto segue a receita.
                  onClick={() => save.mutate(adjustmentFromEdits(consumables, parsed))}
                  className="rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                  {save.isPending ? 'Salvando...' : 'Salvar ajuste'}
                </button>
                <button
                  type="button"
                  onClick={() => setEditing(false)}
                  className="rounded-lg border px-3 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground"
                >
                  Cancelar
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setEditing(true)}
                  className="rounded-lg border px-3 py-1.5 text-sm font-medium hover:bg-muted"
                >
                  Ajustar quantidades
                </button>
                {anyAdjusted && (
                  <button
                    type="button"
                    disabled={save.isPending}
                    onClick={() => save.mutate(null)}
                    className="rounded-lg px-3 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground disabled:opacity-50"
                  >
                    Voltar à receita
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
